import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  IconButton
} from '@mui/material';
import { 
  Lock, 
  Phone, 
  Security, 
  Flag, 
  Visibility, 
  VisibilityOff 
} from '@mui/icons-material';
import * as authService from '../../services/auth';
import PageMeta from '../../components/PageMeta';

const steps = ['Enter Phone Number', 'Verify Code', 'Set New Password'];

// Nigeria country data
const nigeriaCountry = {
  name: 'Nigeria',
  code: 'NG',
  dialCode: '+234',
  flag: '🇳🇬'
};

// Password validation function
const validatePassword = (password) => {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const hasMinLength = password.length >= 8;
  
  return {
    isValid: hasUpperCase && hasLowerCase && hasSpecialChar && hasMinLength,
    hasUpperCase,
    hasLowerCase,
    hasSpecialChar,
    hasMinLength
  };
};

// Phone number formatting function
const formatPhoneNumber = (phone) => {
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  
  // If it starts with 0, remove it
  if (cleaned.startsWith('0')) {
    return cleaned.substring(1);
  }
  
  return cleaned;
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form data
  const [phone, setPhone] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [useEmail, setUseEmail] = useState(false);
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  
  // Password visibility states
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  // Input validation states
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [codeTouched, setCodeTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  
  // Verification state
  const [codeSent, setCodeSent] = useState(false);
  const [codeVerified, setCodeVerified] = useState(false);
  
  // Resend timer
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(true);

  // Password validation state
  const [passwordValidation, setPasswordValidation] = useState({
    isValid: false,
    hasUpperCase: false,
    hasLowerCase: false,
    hasSpecialChar: false,
    hasMinLength: false
  });

  // Helper function to get input color based on validation state
  const getInputColor = (value, touched, isValid = null) => {
    if (!touched) return 'default'; // Black when not touched
    if (isValid !== null) {
      return isValid ? 'success' : 'error'; // Green if valid, red if invalid
    }
    return value.trim() ? 'success' : 'error'; // Green if has value, red if empty
  };

  // Handle input changes and hide notifications
  const handleInputChange = (setter, setTouched) => (e) => {
    setter(e.target.value);
    setTouched(true);
    setError('');
    setSuccess('');
  };

  // Handle phone number change with formatting
  const handlePhoneChange = (e) => {
    const value = e.target.value;
    // Only allow digits
    const formatted = value.replace(/\D/g, '');
    setPhone(formatted);
    setPhoneTouched(true);
    setError('');
    setSuccess('');
  };

  // Handle password change with validation
  const handlePasswordChange = (e) => {
    const password = e.target.value;
    setNewPassword(password);
    setPasswordTouched(true);
    setPasswordValidation(validatePassword(password));
    setError('');
    setSuccess('');
  };

  // Handle confirm password change
  const handleConfirmPasswordChange = (e) => {
    setConfirmPassword(e.target.value);
    setConfirmPasswordTouched(true);
    setError('');
    setSuccess('');
  };

  // Handle verification code change
  const handleCodeChange = (e) => {
    setVerificationCode(e.target.value);
    setCodeTouched(true);
    setError('');
    setSuccess('');
  };

  // Handle email input change
  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setEmailTouched(true);
    setError('');
    setSuccess('');
  };

  // Toggle password visibility
  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Toggle confirm password visibility
  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => {
          if (prev <= 1) {
            setCanResend(true);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendCode = async () => {
    if (useEmail) {
      if (!email.trim()) {
        setError('Please enter your email address');
        return;
      }
      // Basic email validation
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        setError('Please enter a valid email address');
        return;
      }
      setLoading(true);
      try {
        const resp = await authService.sendVerificationCode({ type: 'email', email });
        setCodeSent(true);
        setResendTimer(60);
        setCanResend(false);
        setSuccess(`Verification code sent to your email (${email})`);
        setActiveStep(1);
      } catch (err) {
        setError(err.message || 'Failed to send verification code');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!phone.trim()) {
      setError('Please enter your phone number');
      return;
    }

    // Validate phone number length (should be 10 digits for Nigeria)
    if (phone.length !== 10) {
      setError('Please enter a valid 10-digit Nigerian phone number');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      // Format phone number with country code (no leading +)
      const formattedPhone = nigeriaCountry.dialCode.replace('+', '') + phone;
      const response = await authService.sendVerificationCode({ type: 'sms', phone: formattedPhone });
      
      setCodeSent(true);
      setSuccess(`Verification code sent to ${nigeriaCountry.dialCode} ${phone}`);
      setActiveStep(1);
      // Start resend timer (60 seconds)
      setResendTimer(60);
      setCanResend(false);
    } catch (error) {
      setError(error.message || 'Failed to send verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (useEmail) {
      setLoading(true);
      try {
        const resp = await authService.sendVerificationCode({ type: 'email', email });
        setResendTimer(60);
        setCanResend(false);
        setSuccess(`Verification code resent to your email (${email})`);
      } catch (err) {
        setError(err.message || 'Failed to resend verification code');
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!canResend) return;
    
    setLoading(true);
    setError('');
    
    try {
      // Format phone number with country code (no leading +)
      const formattedPhone = nigeriaCountry.dialCode.replace('+', '') + phone;
      const response = await authService.sendVerificationCode({ type: 'sms', phone: formattedPhone });
      
      setSuccess(`Verification code resent to ${nigeriaCountry.dialCode} ${phone}`);
      // Start resend timer again
      setResendTimer(60);
      setCanResend(false);
    } catch (error) {
      setError(error.message || 'Failed to resend verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError('Please enter the verification code');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      if (useEmail) {
        await authService.verifyCode({ type: 'email', email, code: verificationCode });
      } else {
        // Format phone number with country code (no leading +)
        const formattedPhone = nigeriaCountry.dialCode.replace('+', '') + phone;
        // console.log('Verifying code:', verificationCode, 'phone:', formattedPhone);
        await authService.verifyCode({ type: 'sms', phone: formattedPhone, code: verificationCode });
      }
      setCodeVerified(true);
      setSuccess('Code verified successfully');
      setActiveStep(2);
    } catch (error) {
      setError(error.message || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }
    
    if (!passwordValidation.isValid) {
      setError('Password does not meet requirements');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    
    try {
      if (useEmail) {
        // Use email for reset
        await authService.resetPassword(email, verificationCode, newPassword, true);
      } else {
        // Format phone number with country code (no leading +)
        const formattedPhone = nigeriaCountry.dialCode.replace('+', '') + phone;
        await authService.resetPassword(formattedPhone, verificationCode, newPassword, false);
      }
      setSuccess('Password reset successfully! Redirecting to login...');
      setTimeout(() => {
        navigate('/admin/login');
      }, 2000);
    } catch (error) {
      setError(error.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
      setError('');
      setSuccess('');
    }
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {useEmail
                ? 'Enter your email address to receive a verification code'
                : 'Enter your Nigerian phone number to receive a verification code'}
            </Typography>
            {useEmail ? (
              <TextField
                label="Email Address"
                value={email}
                onChange={handleEmailChange}
                fullWidth
                margin="normal"
                placeholder="e.g., user@example.com"
                disabled={loading}
                color={getInputColor(email, emailTouched)}
                focused={emailTouched}
              />
            ) : (
              <>
                {/* Country Code Selector */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {nigeriaCountry.flag}
                  </Typography>
                  <Typography variant="body2" sx={{ mr: 1 }}>
                    {nigeriaCountry.name}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                    {nigeriaCountry.dialCode}
                  </Typography>
                </Box>
                <TextField
                  label="Phone Number"
                  value={phone}
                  onChange={handlePhoneChange}
                  fullWidth
                  margin="normal"
                  placeholder="e.g., 8123456789"
                  inputProps={{ 
                    maxLength: 10,
                    pattern: '[0-9]*'
                  }}
                  disabled={loading}
                  color={getInputColor(phone, phoneTouched)}
                  focused={phoneTouched}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Typography variant="body2" color="text.secondary">
                          {nigeriaCountry.dialCode}
                        </Typography>
                      </InputAdornment>
                    ),
                  }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  Enter your 10-digit Nigerian phone number (without the country code)
                </Typography>
                {/* Tip for switching to email */}
                {/* <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                  Can't receive SMS?{' '}
                  <Button variant="text" size="small" sx={{ color: '#DC143C', textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => setUseEmail(true)}>
                    Try another method
                  </Button>
                </Typography> */}
              </>
            )}
            <Button
              variant="contained"
              onClick={handleSendCode}
              disabled={loading || (useEmail ? !email : phone.length !== 10)}
              fullWidth
              sx={{ mt: 2, backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Send Verification Code'}
            </Button>
            {/* If using email, allow switching back to phone */}
            {useEmail && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
                Prefer SMS?{' '}
                <Button variant="text" size="small" sx={{ color: '#DC143C', textTransform: 'none', p: 0, minWidth: 0 }} onClick={() => setUseEmail(false)}>
                  Use phone number
                </Button>
              </Typography>
            )}
          </Box>
        );
      
      case 1:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {useEmail 
                ? `Enter the 6-digit verification code sent to ${email}`
                : `Enter the 6-digit verification code sent to ${nigeriaCountry.dialCode} ${phone}`
              }
            </Typography>
            <TextField
              label="Verification Code"
              value={verificationCode}
              onChange={handleCodeChange}
              fullWidth
              margin="normal"
              placeholder="123456"
              inputProps={{ maxLength: 6 }}
              disabled={loading}
              color={getInputColor(verificationCode, codeTouched)}
              focused={codeTouched}
            />
            
            {/* Resend Code Section */}
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              {canResend ? (
                <Button
                  variant="text"
                  onClick={handleResendCode}
                  disabled={loading}
                  sx={{ color: '#DC143C' }}
                >
                  Resend Code
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Resend code in {resendTimer} seconds
                </Typography>
              )}
            </Box>
            
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleVerifyCode}
                disabled={loading || !verificationCode.trim()}
                sx={{ flex: 1, backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Verify Code'}
              </Button>
            </Box>
          </Box>
        );
      
      case 2:
        return (
          <Box sx={{ mt: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Set your new password
            </Typography>
            <TextField
              label="New Password"
              type={showPassword ? 'text' : 'password'}
              value={newPassword}
              onChange={handlePasswordChange}
              fullWidth
              margin="normal"
              placeholder="Enter new password"
              disabled={loading}
              color={getInputColor(newPassword, passwordTouched, passwordValidation.isValid)}
              focused={passwordTouched}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={handleConfirmPasswordChange}
              fullWidth
              margin="normal"
              placeholder="Confirm new password"
              disabled={loading}
              color={getInputColor(confirmPassword, confirmPasswordTouched, newPassword === confirmPassword && confirmPassword.trim())}
              focused={confirmPasswordTouched}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            
            {/* Password Requirements */}
            <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 'bold' }}>
                Password Requirements:
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Typography 
                  variant="caption" 
                  color={passwordValidation.hasMinLength ? 'success.main' : 'text.secondary'}
                >
                  ✓ At least 8 characters
                </Typography>
                <Typography 
                  variant="caption" 
                  color={passwordValidation.hasUpperCase ? 'success.main' : 'text.secondary'}
                >
                  ✓ At least 1 uppercase letter
                </Typography>
                <Typography 
                  variant="caption" 
                  color={passwordValidation.hasLowerCase ? 'success.main' : 'text.secondary'}
                >
                  ✓ At least 1 lowercase letter
                </Typography>
                <Typography 
                  variant="caption" 
                  color={passwordValidation.hasSpecialChar ? 'success.main' : 'text.secondary'}
                >
                  ✓ At least 1 special character
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
                sx={{ flex: 1 }}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleResetPassword}
                disabled={loading || !newPassword.trim() || !confirmPassword.trim() || !passwordValidation.isValid}
                sx={{ flex: 1, backgroundColor: '#DC143C', '&:hover': { backgroundColor: '#B91C3C' } }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
              </Button>
            </Box>
          </Box>
        );
      
      default:
        return null;
    }
  };

  return (
    <>
      <PageMeta
        title="Reset Password"
        description="Reset your Checkstall Import admin password using phone or email verification."
      />
    <Box sx={{ 
      maxWidth: 500, 
      mx: 'auto', 
      mt: 4, 
      p: 3, 
      border: '1px solid #eee', 
      borderRadius: 2, 
      boxShadow: 1,
      backgroundColor: 'white'
    }}>
      <Box sx={{ textAlign: 'center', mb: 3 }}>
        <Security sx={{ fontSize: 48, color: '#DC143C', mb: 2 }} />
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Set/Reset Password
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Use your Nigerian phone number to verify your identity and set a new password
        </Typography>
      </Box>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {renderStepContent(activeStep)}

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="text"
          onClick={() => navigate('/admin/login')}
          sx={{ color: '#DC143C' }}
        >
          Back to Login
        </Button>
      </Box>
    </Box>
    </>
  );
};

export default ResetPassword; 
